// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
/**
 * Default subject + body for the "Share invoice/quote" email modal
 * (`share-document-modal.tsx`), plus the canonical PDF filename.
 *
 * The share email is deliberately MINIMAL: crm-api renders a one-line
 * "<sharer> shared this invoice with you" plus the attached PDF — no logo, no
 * company chrome, no amount/details box, no footer (see xop-brain
 * `DocumentMailer#share_document` + `docs/crm-api.md`). So the message field
 * defaults to EMPTY: whatever the sender types becomes an optional note shown
 * verbatim under that line; leaving it blank keeps the email to just the line
 * and the PDF.
 */
export interface ShareDocumentDefaults {
  subject: string
  body: string
}

/** Sender company prefix on every share subject. XOP is the tenant; hardcoded
 *  to match the user's requested format (kept in lockstep with the crm-api
 *  `Invoice#share_email_subject` fallback).
 *
 *  THE PORTAL PASSES ITS OWN. In this app the document is the RESELLER's, so
 *  the subject of the email carrying it names the reseller — see the
 *  `senderName` parameter below. This default is what an XOP-issued document
 *  gets, which keeps the file a drop-in for its source. */
export const SENDER = "XOP Networks"

/**
 * The company named in the share subject + PDF filename is the document's
 * SHIP-TO company (the user-specified format — e.g. "XOP Networks Invoice
 * Flexenet 700-0431-009"), falling back to the customer company for records
 * without one. Kept in lockstep with crm-api `Invoice#share_document_company`.
 */
export function shareDocumentCompanyName(record: {
  ship_to_company?: string | null
  customer_company_name?: string | null
  customer?: { company_name?: string | null } | null
}): string | undefined {
  const company =
    record.ship_to_company?.trim() ||
    record.customer_company_name?.trim() ||
    record.customer?.company_name?.trim()
  return company && company.length > 0 ? company : undefined
}

/**
 * Default subject for the share email: "<sender> Invoice <company> <number>"
 * (or "… Quote …" for quotes). `companyName` is the document's ship-to company
 * (see shareDocumentCompanyName); any blank part is dropped so the subject
 * never has double spaces or a dangling label. Body defaults to empty (see above).
 *
 * `senderName` is the company the email is FROM. It defaults to XOP, which is
 * right in the command center where every document is XOP's — and is passed
 * explicitly by this portal, where the document is the reseller's and its
 * customer has no relationship with XOP. Passing "" drops the prefix entirely,
 * which is what the portal does before it knows the issuer: a subject with no
 * sender is neutral, and one naming the wrong company is not.
 */
export function shareDocumentDefaults(
  documentType: "invoice" | "quote",
  documentNumber?: string,
  companyName?: string,
  senderName: string = SENDER,
): ShareDocumentDefaults {
  const label = documentType === "quote" ? "Quote" : "Invoice"
  const parts = [senderName?.trim(), label, companyName?.trim(), documentNumber?.trim()].filter(
    (part): part is string => Boolean(part && part.length > 0),
  )
  return {
    subject: parts.join(" "),
    body: "",
  }
}

/**
 * Filename for the invoice/quote PDF — both the share-email attachment and
 * the download: "Invoice <company> <number>.pdf" (e.g. "Invoice Flexenet
 * 700-0431-009.pdf"). Deliberately NOT the email subject: the file carries no
 * "XOP Networks" sender prefix. Blank parts drop out; characters that are
 * illegal in filenames collapse to "-". Kept in lockstep with crm-api
 * `Invoice#share_pdf_filename`.
 */
export function shareDocumentPdfFilename(
  documentType: "invoice" | "quote",
  documentNumber?: string,
  companyName?: string,
): string {
  const label = documentType === "quote" ? "Quote" : "Invoice"
  const parts = [label, companyName?.trim(), documentNumber?.trim()].filter(
    (part): part is string => Boolean(part && part.length > 0),
  )
  return `${parts.join(" ").replace(/[/\\:*?"<>|]+/g, "-")}.pdf`
}
